import { Grid2 } from "@mui/material";
import { useWatch } from "react-hook-form";
import { evaluateRule, stripHtml } from "./helper";
import { useEffect, useState } from "react";
import { getChoicesForQuestions } from "../../../services/subjectService";

const FieldRenderer = ({
  field,
  index,
  control,
  register,
  errors,
  componentMap,
  currentFields,
  isPanel = false,
  hardStop = false,
}) => {
  const allValues = useWatch({ control });

  const [choices, setChoices] = useState([]);

  useEffect(() => {
    if (
      (field?.type === "dropdown" || field?.type === "select") &&
      field?.receivedId
    ) {
      getChoices(field?.receivedId);
    } else {
      choices && choices?.length > 0 && setChoices([]);
    }
  }, [field?.receivedId]);

  // This is for getting dynamic choices options from API based on questionId
  const getChoices = async (questionId) => {
    try {
      const res = await getChoicesForQuestions(questionId);
      if (
        res?.data?.message === "Choices Extracted" &&
        res?.data?.result &&
        res?.data?.result?.length > 0
      ) {
        setChoices(
          res?.data?.result?.map((item) => {
            return {
              value: item?.value,
              text: stripHtml(item?.choiceDisplayText),
            };
          }),
        );
      }
    } catch (err) {
      console.log("Error fetching choices for question ", field.id, err);
    }
  };

  if (field.type === "panel" && Array.isArray(field.elements)) {
    return (
      <Grid2
        key={index}
        size={{ xs: 12 }}
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          mb: 3,
        }}
      >
        {/* Alignment wrapper */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            justifyContent: "center",
            width: "100%",
          }}
        >
          {/* Label spacer (aligns panel with other fields) */}
          <div
            style={{
              flex: "0 0 200px", // same as normal label width
            }}
          />

          {/* Panel box */}
          <div
            style={{
              flex: 1,
              maxWidth: "400px", // same as input width
              border: "1px solid #dcdcdc",
              borderRadius: "10px",
              padding: "18px",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              background: "#fff",
            }}
          >
            {/* NO TITLE */}

            {/* Stacked fields */}
            {field.elements.map((childField, childIndex) => (
              <FieldRenderer
                key={`${childField.id}_${childField.receivedId}`}
                field={childField}
                index={childIndex}
                control={control}
                register={register}
                isPanel={true}
                errors={errors}
                componentMap={componentMap}
                currentFields={field.elements}
                hardStop={hardStop}
              />
            ))}
          </div>
        </div>
      </Grid2>
    );
  }

  const isDateField = field?.inputType === "date" && field?.type !== "eq5d5l";
  const isTimeField = field?.inputType === "time";

  const Component =
    componentMap[isDateField ? "Date" : isTimeField ? "Time" : field.type];
  if (!Component) return null;

  const uniqueName = `${field.name}_${field?.receivedId}`;
  const isHtmlLabel =
    typeof field.label === "string" && /<\/?[a-z][\s\S]*>/i.test(field.label);

  let shouldRender = true;
  let shouldDisable = false;

  if (field.forceDisabled) {
    shouldDisable = true;
  }

  if (hardStop) {
    shouldDisable = true;
  }

  /* -------- visibleIf -------- */
  if (field?.visibleIf) {
    shouldRender = evaluateRule(allValues, currentFields, field.visibleIf);
  }

  /* -------- disableIf -------- */
  if (field?.enableIf) {
    const disableResult = evaluateRule(
      allValues,
      currentFields,
      field.enableIf,
    );
    if (!disableResult) shouldDisable = true;
  }

  if (!shouldRender) return null;

  return (
    <Grid2
      key={index}
      size={{ xl: 12, md: 12, lg: 12, sm: 12, xs: 12 }}
      sx={{ display: "flex", flexDirection: "column", width: "100%", mb: 3 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "16px",
          justifyContent: "center",
        }}
      >
        {/* Label */}
        <div
          style={{
            flex: isPanel ? "0 0 100px" : "0 0 250px",
            textAlign: isPanel ? "left" : "right",
            wordWrap: "break-word",
            paddingTop: "8px",
          }}
        >
          {isHtmlLabel ? (
            <>
              <span
                dangerouslySetInnerHTML={{ __html: field.label }}
                style={{ display: "inline-block" }}
              />
              {field?.required && (
                <span style={{ color: "red", marginLeft: "2px" }}>*</span>
              )}
            </>
          ) : (
            <>
              {field.label || field?.title || field?.name}
              {field?.isRequired && (
                <span style={{ color: "red", marginLeft: "2px" }}>*</span>
              )}
            </>
          )}
        </div>

        {/* Component + Error */}
        <div
          style={{
            flex: 1,
            maxWidth: "400px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Component
            label={field.label || field?.radioLabel || field?.title}
            name={uniqueName}
            props={field.props}
            register={register}
            control={control}
            options={
              choices?.length > 0 ? choices : field?.options || field?.choices
            }
            isRequired={field?.isRequired}
            disabled={shouldDisable}
            specialProps={
              field?.specialProps ||
              field?.text ||
              field?.content ||
              field?.onlyToday ||
              field?.checkboxLabel ||
              ((field?.maxDate || field?.minDate) && {
                maxDate: field?.maxDate,
                minDate: field?.minDate,
              }) ||
              ((field?.maxValue || field?.minValue) && {
                minValue: field?.minValue,
                maxValue: field?.maxValue,
              })
              // ||
              // field?.checkboxLabel
            }
          />

          {/* Error Message */}
          {/* {field.type === "ageDateRange" ? (
            <div
              style={{
                display: "flex",
                justifyContent:
                  errors[uniqueName]?.start && errors[uniqueName]?.end
                    ? "space-between"
                    : errors[uniqueName]?.end
                      ? "flex-end"
                      : "flex-start",
                gap: "10px",
                marginTop: "4px",
              }}
            >
              {errors[uniqueName]?.start && (
                <div style={{ color: "red", fontSize: "0.8rem" }}>
                  {errors[uniqueName]?.start?.message}
                </div>
              )}
              {errors[uniqueName]?.end && (
                <div style={{ color: "red", fontSize: "0.8rem" }}>
                  {errors[uniqueName]?.end?.message}
                </div>
              )}
              {errors[uniqueName] && (
                <div
                  style={{
                    color: "red",
                    fontSize: "0.8rem",
                    marginTop: "4px",
                  }}
                >
                  {errors[uniqueName]?.message}
                </div>
              )}
            </div>
          ) : (
            errors[uniqueName] && (
              <div
                style={{
                  color: "red",
                  fontSize: "0.8rem",
                  marginTop: "4px",
                }}
              >
                {errors[uniqueName]?.message}
              </div>
            )
          )} */}
          {errors[uniqueName] && (
            <div
              style={{
                color: "red",
                fontSize: "0.8rem",
                marginTop: "4px",
              }}
            >
              {errors[uniqueName]?.message}
            </div>
          )}
        </div>
      </div>
    </Grid2>
  );
};

export default FieldRenderer;
